import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, MessageSquare, CheckSquare, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { formatRelative } from "@/lib/format";
import { cn } from "@/lib/utils";

/**
 * In-app notification bell for the top nav.
 *
 * Polls GET /notifications every 60s. The badge shows the count of things
 * that need attention right now — unread conversations plus tasks due — which
 * is the single number a rep actually acts on (raw message count is noisier
 * and less actionable). Clicking opens a popover listing recent items; message
 * items deep-link to the inbox conversation, task items to the tasks page.
 */

interface NotificationCounts {
  unread_conversations: number;
  unread_messages: number;
  tasks_due: number;
  new_contacts: number;
}

interface NotificationItem {
  kind: "message" | "task";
  id: string;
  title: string;
  body?: string;
  channel?: string;
  contact_id?: string;
  at: string;
  count?: number;
}

interface NotificationsResponse {
  counts: NotificationCounts;
  items: NotificationItem[];
}

const POLL_MS = 60_000;

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const [data, setData] = useState<NotificationsResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const ref = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Poll the notifications endpoint. Runs immediately, then every 60s.
  // The interval is torn down on unmount (cleanup return).
  useEffect(() => {
    let alive = true;
    const load = async () => {
      try {
        const d = await api.get<NotificationsResponse>("/notifications");
        if (alive) setData(d);
      } catch {
        /* transient — keep the last good snapshot */
      } finally {
        if (alive) setLoading(false);
      }
    };
    void load();
    const timer = setInterval(load, POLL_MS);
    return () => {
      alive = false;
      clearInterval(timer);
    };
  }, []);

  // Click-outside + Escape close the popover.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onClick);
      document.removeEventListener("keydown", onKey);
    };
  }, []);

  const counts = data?.counts;
  const badge = (counts?.unread_conversations ?? 0) + (counts?.tasks_due ?? 0);
  const items = data?.items ?? [];

  const openItem = (item: NotificationItem) => {
    setOpen(false);
    if (item.kind === "message") {
      navigate(item.contact_id ? `/inbox/${item.contact_id}` : "/inbox");
    } else {
      navigate("/tasks");
    }
  };

  return (
    <div ref={ref} className="relative shrink-0">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-label={badge > 0 ? `Notifications, ${badge} unread` : "Notifications"}
        title="Notifications"
        className={cn(
          "relative inline-flex h-8 w-8 items-center justify-center rounded-lg border border-zinc-800 bg-zinc-900/50 text-zinc-400 transition-colors hover:bg-zinc-900 hover:text-zinc-100",
          open && "bg-zinc-900 text-zinc-100"
        )}
      >
        <Bell className="h-4 w-4" />
        {badge > 0 && (
          <span className="absolute -right-1 -top-1 inline-flex h-4 min-w-4 items-center justify-center rounded-full bg-rose-500 px-1 text-[10px] font-semibold leading-none text-white ring-2 ring-zinc-950">
            {badge > 99 ? "99+" : badge}
          </span>
        )}
      </button>

      {open && (
        <div className="absolute right-0 top-full mt-1 w-96 max-w-[92vw] rounded-xl border border-zinc-800 bg-zinc-950 shadow-xl shadow-shade/40 animate-fade-in z-40">
          <div className="flex items-center justify-between border-b border-zinc-800 px-3 py-2">
            <span className="text-sm font-semibold text-zinc-100">Notifications</span>
            {badge > 0 && (
              <span className="text-[11px] font-medium text-rose-300">{badge} need attention</span>
            )}
          </div>

          {counts && (
            <div className="grid grid-cols-3 gap-px border-b border-zinc-800 bg-zinc-800/40 text-center">
              <SummaryStat label="Unread" value={counts.unread_conversations} />
              <SummaryStat label="Tasks due" value={counts.tasks_due} />
              <SummaryStat label="New contacts" value={counts.new_contacts} />
            </div>
          )}

          <div className="max-h-80 overflow-y-auto p-1">
            {loading && !data && (
              <div className="flex items-center gap-2 p-4 text-xs text-zinc-500">
                <Loader2 className="h-3.5 w-3.5 animate-spin" />
                Loading…
              </div>
            )}
            {!loading && items.length === 0 && (
              <div className="flex flex-col items-center gap-1.5 px-3 py-8 text-center">
                <Bell className="h-6 w-6 text-zinc-600" />
                <p className="text-xs text-zinc-500">You're all caught up.</p>
              </div>
            )}
            {items.map((item) => {
              const Icon = item.kind === "message" ? MessageSquare : CheckSquare;
              return (
                <button
                  key={`${item.kind}-${item.id}`}
                  type="button"
                  onClick={() => openItem(item)}
                  className="flex w-full items-start gap-2.5 rounded-lg px-2.5 py-2 text-left hover:bg-zinc-900"
                >
                  <span
                    className={cn(
                      "mt-0.5 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-lg",
                      item.kind === "message"
                        ? "bg-rose-500/15 text-rose-300"
                        : "bg-amber-500/15 text-amber-300"
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className="truncate text-sm font-medium text-zinc-100">{item.title}</p>
                      {item.count && item.count > 1 && (
                        <span className="shrink-0 rounded-full bg-zinc-800 px-1.5 text-[10px] font-medium text-zinc-300 tabular-nums">
                          {item.count}
                        </span>
                      )}
                    </div>
                    {item.body && (
                      <p className="truncate text-xs text-zinc-500">{item.body}</p>
                    )}
                    <div className="mt-0.5 flex items-center gap-1.5 text-[10px] text-zinc-600">
                      {item.channel && <span className="uppercase tracking-wide">{item.channel}</span>}
                      {item.channel && <span>·</span>}
                      <span>{formatRelative(item.at)}</span>
                    </div>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryStat({ label, value }: { label: string; value: number }) {
  return (
    <div className="bg-zinc-950 px-2 py-2">
      <div className="text-base font-semibold text-zinc-100 tabular-nums">{value}</div>
      <div className="text-[10px] uppercase tracking-wide text-zinc-500">{label}</div>
    </div>
  );
}
